import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { EditOutlined } from '@ant-design/icons';
import { Button, Card, Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import { TableSecondaryText } from 'styles';

import { TableTimestamp } from 'components';
import AuditLog from 'components/AuditLog';
import RenderInGrid from 'components/Layout/RenderInGrid';
import { GridContainer, Tag } from 'components/Layout/common';
import { ImageItem } from 'components/Layout/list';
import Label from 'components/Text/Label';
import { ActionsBtnGroup, SectionHeader } from 'containers/BDO/utils';
import { IParamsWithContainId } from 'utils/types';

import { getBDOSetting } from '../action';
import AddEditVouchersModal from './AddEditVouchersModal';

const VouchersShow = () => {
  const [isEdit, setIsEdit] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const params = useParams<IParamsWithContainId>();
  const vouchersId = params.id;

  const { bdoSetting, loading, error } = useSelector((state: any) => state.bdoSettings);

  useEffect(() => {
    if (vouchersId) {
      dispatch(getBDOSetting(vouchersId));
    }
  }, [vouchersId, dispatch]);

  const handleEditClick = () => {
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDuplicateClick = () => {
    setIsEdit(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderInGrid = (description, content, hasContentChanged?: boolean) => {
    return (
      <RenderInGrid
        description={description}
        content={content}
        hasContentChanged={hasContentChanged}
      />
    );
  };

  const renderTags = (tags = []) => {
    if (isEmpty(tags)) {
      if (!tags.length) {
        return <Label>N.A</Label>;
      }
    }
    if (!isEmpty(tags)) {
      return tags.map(tag => {
        return tag?.id ? (
          <Tag key={tag.id}>
            <Label nomargin>{tag.name}</Label>
          </Tag>
        ) : null;
      });
    } else {
      return <Label>N.A</Label>;
    }
  };

  return (
    <>
      <Card>
        <Row justify="space-between">
          <SectionHeader>AMEX Voucher Details</SectionHeader>
          <ActionsBtnGroup>
            <Button size="large" type="primary" ghost onClick={handleDuplicateClick}>
              Duplicate
            </Button>
            <Button size="large" type="primary" icon={<EditOutlined />} onClick={handleEditClick}>
              Edit
            </Button>
          </ActionsBtnGroup>
        </Row>
        {renderInGrid('Name:', <Label dark>{bdoSetting?.name}</Label>)}
        {renderInGrid('Type:', <Label dark>{bdoSetting?.item_type}</Label>)}
        {renderInGrid(
          'Image:',
          <ImageItem>
            <img src={bdoSetting?.image?.url} alt={bdoSetting?.name} />
          </ImageItem>
        )}
        {renderInGrid('Cost:', <Label dark>{bdoSetting?.cost} points</Label>)}
        {renderInGrid('Category:', <Label dark>{bdoSetting?.category}</Label>)}
        {renderInGrid('LRS Reward Code:', <Label dark>{bdoSetting?.lrs_reward_code}</Label>)}
        {renderInGrid('Applicable Credit Cards:', renderTags(bdoSetting?.cards))}
        {renderInGrid(
          'Start & End Date:',
          <Label dark>
            <Col>
              <GridContainer columns="auto 10px auto" gap="10px" inline>
                {bdoSetting?.start_date ? (
                  <TableTimestamp timestamp={bdoSetting?.start_date} />
                ) : (
                  'N/A'
                )}
                <TableSecondaryText>~</TableSecondaryText>
                {bdoSetting?.end_date ? <TableTimestamp timestamp={bdoSetting?.end_date} /> : 'N/A'}
              </GridContainer>
            </Col>
          </Label>
        )}
      </Card>
      <AuditLog
        entity="bdo_transfer_request_card_item"
        id={vouchersId}
        isChanges2
        key={bdoSetting?.id}
      />
      <AddEditVouchersModal
        modalOpen={isModalOpen}
        isEdit={isEdit}
        closeModal={handleCloseModal}
        selectedVouchers={bdoSetting?.id}
      />
    </>
  );
};

export default VouchersShow;
